import { mockApi } from "./mockApi";
import type { EventView, RunDifficultyLevel, RunMode, RunResultView, UserView } from "./types";

const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, "") ??
  "http://127.0.0.1:8787";
const USE_MOCK_API = API_BASE === "mock";

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? `request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(`request failed (${res.status})`);
  return res.json() as Promise<T>;
}

export const api = {
  base: API_BASE,
  newRun: (opts: {
    mode: RunMode;
    lang?: string;
    themePrompt?: string;
    seed?: number;
    userId?: string;
    difficulty?: RunDifficultyLevel;
  }) =>
    USE_MOCK_API
      ? mockApi.newRun(opts)
      : post<{ runId: string; view: EventView }>("/api/runs", opts),
  upsertUser: (opts: { id: string; name: string }) =>
    USE_MOCK_API ? mockApi.upsertUser(opts) : post<{ user: UserView }>("/api/users", opts),
  userResults: (id: string) =>
    USE_MOCK_API ? mockApi.userResults(id) : get<{ results: RunResultView[] }>(`/api/users/${id}/results`),
  getRun: (id: string) => (USE_MOCK_API ? mockApi.getRun(id) : get<{ view: EventView }>(`/api/runs/${id}`)),
  select: (id: string, actionId: string, numDice: number) =>
    USE_MOCK_API
      ? mockApi.select(id, actionId, numDice)
      : post<{ view: EventView }>(`/api/runs/${id}/select`, { actionId, numDice }),
  roll: (id: string, numDice: number) =>
    USE_MOCK_API ? mockApi.roll(id, numDice) : post<{ view: EventView }>(`/api/runs/${id}/roll`, { numDice }),
  continue: (id: string) =>
    USE_MOCK_API ? mockApi.continue(id) : post<{ view: EventView }>(`/api/runs/${id}/continue`, {}),
};
